import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  FileText, Receipt, ArrowRight, LogOut, Bot,
  AlertTriangle, CheckCircle
} from 'lucide-react';
import GlassPanel from '../../components/GlassPanel';
import EmptyState from '../../components/EmptyState';

const clientSubNav = [
  { path: '/client', label: 'Dashboard' },
  { path: '/client/quotes', label: 'Quotes' },
  { path: '/client/invoices', label: 'Invoices' },
  { path: '/client/emergency', label: 'Report Emergency', emergency: true },
];

const mockClientData = {
  name: 'John Smith',
  email: 'john.smith@email.com',
  pendingQuotes: [
    { id: 234, title: 'Garden Landscaping', amount: 4140, date: '2024-01-12' }
  ],
  outstandingInvoices: [
    { id: 1045, title: 'Kitchen Refit - Final Payment', amount: 5100, dueDate: '2024-01-29' }
  ]
};

// Emergency types - simple dropdown options
const emergencyTypes = [
  { value: 'burst_pipe', label: 'Burst Pipe / Flooding' },
  { value: 'electrics_out', label: 'Electrics Out' },
  { value: 'gas_issue', label: 'Gas Issue' },
  { value: 'heating_failure', label: 'Heating Failure' },
  { value: 'structural_safety', label: 'Structural Safety Risk' },
  { value: 'other', label: 'Other Emergency' },
];

// Client AI Help Component - explains only, cannot accept or change anything
function ClientAIHelp() {
  return (
    <div className="client-ai-help">
      <div className="client-ai-help-icon">
        <Bot size={20} />
      </div>
      <div className="client-ai-help-content">
        <h4>Need help understanding this?</h4>
        <p>AI explains only. It cannot accept or change anything.</p>
      </div>
      <button disabled title="AI explains only. It cannot accept or change anything.">
        Ask AI to explain
      </button>
    </div>
  );
}

// Emergency Report Page - Minimal, Calm, No urgency theatrics
function EmergencyReportPage() {
  const [formData, setFormData] = useState({
    emergency_type: '',
    description: '',
    contact_name: '',
    contact_phone: '',
    contact_email: '',
    address: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [emergencyId, setEmergencyId] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    const mockId = `EM-${Date.now().toString(36).toUpperCase()}`;
    setEmergencyId(mockId);
    setSubmitted(true);
  };

  // Submission Result State - Confirmation only
  if (submitted) {
    return (
      <div data-testid="emergency-confirmation">
        <GlassPanel>
          <div style={{ textAlign: 'center', padding: 'var(--space-xl)' }}>
            <CheckCircle size={48} style={{ color: 'var(--status-success)', marginBottom: 'var(--space-md)' }} />
            
            <h2 style={{ color: 'var(--text-primary)', marginBottom: 'var(--space-md)', fontSize: '1.25rem' }}>
              Your emergency has been logged and escalated.
            </h2>
            
            <p style={{ color: 'var(--text-muted)', marginBottom: 'var(--space-lg)', fontSize: '0.9375rem' }}>
              Reference: <strong>{emergencyId}</strong>
            </p>
            
            {/* No timelines, No guarantees, No status tracking shown to client */}
            <div style={{
              background: 'var(--bg-tertiary)',
              padding: 'var(--space-lg)',
              borderRadius: 'var(--radius-md)',
              textAlign: 'left',
              marginTop: 'var(--space-lg)'
            }}>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', margin: 0, lineHeight: 1.6 }}>
                <strong>Important:</strong> No attendance time has been promised. No works have been authorised. 
                Someone will contact you as soon as possible.
              </p>
            </div>
            
            <div style={{
              marginTop: 'var(--space-lg)',
              padding: 'var(--space-md)',
              background: 'var(--status-warning-bg)',
              borderRadius: 'var(--radius-md)',
              textAlign: 'left'
            }}>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.8125rem', margin: 0 }}>
                If you smell gas, leave the property and call: <strong>0800 111 999</strong><br />
                If there is immediate danger, call: <strong>999</strong>
              </p>
            </div>
          </div>
        </GlassPanel>
      </div>
    );
  }

  // Emergency Report Form - Minimal UI
  return (
    <div data-testid="emergency-form">
      <div style={{ marginBottom: 'var(--space-lg)' }}>
        <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--text-primary)', margin: '0 0 var(--space-xs)' }}>
          Report an Emergency
        </h2>
        <p style={{ color: 'var(--text-muted)', margin: 0, fontSize: '0.875rem' }}>
          Use this form for genuine emergencies only.
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <GlassPanel>
          {/* Emergency Type - Dropdown */}
          <div className="form-group">
            <label className="form-label">Emergency Type</label>
            <select
              className="form-input"
              value={formData.emergency_type}
              onChange={(e) => setFormData(prev => ({ ...prev, emergency_type: e.target.value }))}
              required
              data-testid="emergency-type-select"
            >
              <option value="">Select type...</option>
              {emergencyTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>

          {/* Description - Text */}
          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              className="form-input"
              placeholder="Describe the emergency..."
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={4}
              required
              data-testid="emergency-description"
            />
          </div>

          {/* Contact Details */}
          <div className="responsive-grid-2" style={{ gap: 'var(--space-md)' }}>
            <div className="form-group">
              <label className="form-label">Your Name</label>
              <input
                type="text"
                className="form-input"
                placeholder="Full name"
                value={formData.contact_name}
                onChange={(e) => setFormData(prev => ({ ...prev, contact_name: e.target.value }))}
                required
                data-testid="emergency-contact-name"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Phone Number</label>
              <input
                type="tel"
                className="form-input"
                placeholder="07xxx xxxxxx"
                value={formData.contact_phone}
                onChange={(e) => setFormData(prev => ({ ...prev, contact_phone: e.target.value }))}
                required
                data-testid="emergency-contact-phone"
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Email (optional)</label>
            <input
              type="email"
              className="form-input"
              placeholder="email@example.com"
              value={formData.contact_email}
              onChange={(e) => setFormData(prev => ({ ...prev, contact_email: e.target.value }))}
              data-testid="emergency-contact-email"
            />
          </div>

          {/* Attachments - Optional */}
          <div className="form-group">
            <label className="form-label">Attachments (optional)</label>
            <input
              type="file"
              className="form-input"
              multiple
              data-testid="emergency-attachments"
              style={{ padding: 'var(--space-sm)' }}
            />
          </div>

          {/* Submit */}
          <button 
            type="submit" 
            className="btn btn-primary w-full"
            disabled={!formData.emergency_type || !formData.description || !formData.contact_name || !formData.contact_phone}
            data-testid="emergency-submit"
          >
            Submit Emergency Report
          </button>

          <p style={{ fontSize: '0.75rem', color: 'var(--text-dimmed)', marginTop: 'var(--space-md)', textAlign: 'center' }}>
            By submitting, you confirm this is a genuine emergency.
          </p>
        </GlassPanel>
      </form>
    </div>
  );
}

export function ClientPortal() {
  const location = useLocation();
  const isEmergencyPage = location.pathname === '/client/emergency';

  return (
    <div className="client-portal">
      <header className="client-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
          <h1 style={{ fontFamily: 'var(--font-family-display)', fontSize: '1.25rem', fontWeight: '700' }}>
            Trade<span style={{ color: 'var(--accent-primary)' }}>Life</span>
          </h1>
          <span style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Client Portal</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
          <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>{mockClientData.name}</span>
          <button className="btn btn-ghost btn-icon" style={{ color: 'var(--text-muted)' }}>
            <LogOut size={18} />
          </button>
        </div>
      </header>

      <div className="client-content">
        {/* Sub Navigation */}
        <div className="client-nav">
          {clientSubNav.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`client-nav-link ${location.pathname === item.path ? 'active' : ''}`}
              style={item.emergency ? {
                background: location.pathname === item.path ? 'var(--status-danger)' : 'transparent',
                borderColor: 'var(--status-danger)',
                color: location.pathname === item.path ? 'white' : 'var(--status-danger)'
              } : {}}
              data-testid={`nav-${item.path.replace('/client/', '') || 'dashboard'}`}
            >
              {item.emergency && <AlertTriangle size={14} style={{ marginRight: '4px' }} />}
              {item.label}
            </Link>
          ))}
        </div>

        {/* Content based on route */}
        {isEmergencyPage ? (
          <EmergencyReportPage />
        ) : (
          <>
            {/* Welcome */}
            <div style={{ marginBottom: 'var(--space-xl)' }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: '600', color: 'var(--text-primary)', marginBottom: 'var(--space-xs)' }}>
                Welcome, {mockClientData.name}
              </h2>
              <p style={{ color: 'var(--text-muted)' }}>View your quotes and invoices below</p>
            </div>

            <div className="responsive-grid-2" style={{ gap: 'var(--space-lg)' }}>
              {/* Pending Quotes */}
              <GlassPanel title="Pending Quotes" titleIcon={FileText}>
                {mockClientData.pendingQuotes.length > 0 ? (
                  <div className="list-container">
                    {mockClientData.pendingQuotes.map((quote) => (
                      <Link key={quote.id} to={`/client/quotes`} className="list-item">
                        <div className="list-item-content">
                          <div className="list-item-title">Quote #{quote.id}</div>
                          <div className="list-item-subtitle">{quote.title}</div>
                        </div>
                        <div className="list-item-meta">
                          <span className="list-item-amount">£{quote.amount.toLocaleString()}</span>
                          <ArrowRight size={16} style={{ color: 'var(--accent-primary)' }} />
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <EmptyState title="No pending quotes" description="Quotes requiring your action will appear here" showGraphic={false} />
                )}
              </GlassPanel>

              {/* Outstanding Invoices */}
              <GlassPanel title="Outstanding Invoices" titleIcon={Receipt}>
                {mockClientData.outstandingInvoices.length > 0 ? (
                  <div className="list-container">
                    {mockClientData.outstandingInvoices.map((invoice) => (
                      <Link key={invoice.id} to={`/client/invoices`} className="list-item">
                        <div className="list-item-content">
                          <div className="list-item-title">Invoice #{invoice.id}</div>
                          <div className="list-item-subtitle">Due: {invoice.dueDate}</div>
                        </div>
                        <div className="list-item-meta">
                          <span className="list-item-amount">£{invoice.amount.toLocaleString()}</span>
                          <ArrowRight size={16} style={{ color: 'var(--status-warning)' }} />
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <EmptyState title="No outstanding invoices" description="All paid up!" showGraphic={false} />
                )}
              </GlassPanel>
            </div>

            {/* Client AI Help */}
            <ClientAIHelp />
          </>
        )}
      </div>
    </div>
  );
}

export default ClientPortal;
